// src/hooks/useProductSales.ts - Complet avec système d'archivage, nouvelle logique promotions ET support transactions

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  increment,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { handleFirestoreError } from '../utils/firestoreErrorHandler';
import { ProductSale, ProductSalePaymentStatus, InventoryFilters } from '../types/inventory';
import { PrestationPaymentMethod, Client, PaymentSplit } from '../types/entities';
import { getPrimaryPaymentMethod } from '../utils/paymentUtils';
import { getProductDisplayName } from '../utils/productPackaging';
import { calculateLoyaltyBonus } from '../constants/loyalty';
import { useAuth } from '../contexts/AuthContext';
import { getSalesSiteUiLabels, isLoyaltyEnabled } from '../types/business';
import { getProductStockAtSite } from '../utils/productStock';
import {
  marketingRevenueForPackAwareLine,
  shouldApplyMarketingPromotionForLine,
} from '../utils/catalogPacks';
import { useCreateStockNotification } from './useNotifications';
import { useApplyPromotion } from './useMarketingCampaigns';
import { usePermissions } from './usePermissions';
import { matchesSearchInFields } from '../utils/searchUtils';
import { useDefaultPointOfSaleSite } from './useSites';
import { getStockAlertThresholdForSite } from '../utils/stockAlerts';
import { AppError } from '../utils/i18n/AppError';
import {
  averageAmount,
  billedTtcForProductSale,
} from '../utils/cashReports/cashInAmounts';

// ===== HOOKS POUR LES VENTES DE PRODUITS =====

export const useProductSales = (filters?: InventoryFilters) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['inventory', 'productSales', businessId, filters?.employeeId,
    filters?.dateRange?.[0]?.getTime(),
    filters?.dateRange?.[1]?.getTime(),
    filters?.searchTerm,
    filters?.transactionGroupId,
    filters?.includeArchived,
    filters?.siteId],
    queryFn: async () => {
      console.log('🔍 useProductSales - Début de la requête', { businessId, filters });
      
      if (!businessId) {
        console.warn('Aucun businessId disponible');
        return [];
      }

      try {
        let q;

        // NOUVEAU : Si on filtre par transactionGroupId
        if (filters?.transactionGroupId && filters.transactionGroupId.trim() !== '') {
          q = query(
            collection(db, COLLECTIONS.productSales),
            where('businessId', '==', businessId),
            where('transactionGroupId', '==', filters.transactionGroupId),
            orderBy('transactionIndex', 'asc') // Ordre par index de transaction
          );
        }
        // STRATÉGIE 1: Si on filtre par employé ET par période de dates
        else if (filters?.employeeId && filters?.dateRange) {
          q = query(
            collection(db, COLLECTIONS.productSales),
            where('businessId', '==', businessId),
            where('employeeId', '==', filters.employeeId),
            where('date', '>=', Timestamp.fromDate(filters.dateRange[0])),
            where('date', '<=', Timestamp.fromDate(filters.dateRange[1])),
            orderBy('date', 'desc')
          );
        }
        // STRATÉGIE 2: Si on filtre seulement par employé
        else if (filters?.employeeId) {
          q = query(
            collection(db, COLLECTIONS.productSales),
            where('businessId', '==', businessId),
            where('employeeId', '==', filters.employeeId),
            orderBy('date', 'desc'),
            limit(100)
          );
        }
        // STRATÉGIE 3: Si on filtre seulement par période
        else if (filters?.dateRange) {
          q = query(
            collection(db, COLLECTIONS.productSales),
            where('businessId', '==', businessId),
            where('date', '>=', Timestamp.fromDate(filters.dateRange[0])),
            where('date', '<=', Timestamp.fromDate(filters.dateRange[1])),
            orderBy('date', 'desc')
          );
        }
        // STRATÉGIE 4: Filtre par institut seulement
        else {
          q = query(
            collection(db, COLLECTIONS.productSales),
            where('businessId', '==', businessId),
            orderBy('date', 'desc'),
            limit(100)
          );
        }
        console.log('🔥 Executing Firebase query with employeeId:', filters?.employeeId);
        const snapshot = await getDocs(q).catch(error => {
          handleFirestoreError(error, 'useProductSales - Main query');
          throw error;
        });
        
        let sales = snapshot.docs.map(doc => {
          const data = doc.data();
          const finalPrice = data.finalPrice || data.totalPrice;
          const amountPaid = data.amountPaid ?? finalPrice ?? 0;
          const amountRemaining = data.amountRemaining ?? Math.max((finalPrice ?? 0) - amountPaid, 0);
          return {
            id: doc.id,
            businessId: data.businessId,
            productId: data.productId,
            productName: data.productName,
            quantity: data.quantity,
            unitPrice: data.unitPrice,
            totalPrice: data.totalPrice,
            finalPrice: data.finalPrice || data.totalPrice,
            clientId: data.clientId,
            clientName: data.clientName,
            employeeId: data.employeeId,
            employeeName: data.employeeName,
            date: data.date?.toDate(),
            paymentMethod: data.paymentMethod,
            paymentSplits: data.paymentSplits as PaymentSplit[] | undefined, // ✅ NOUVEAU : Récupérer paymentSplits depuis Firestore
            loyaltyBonusGranted: data.loyaltyBonusGranted || 0,
            loyaltyAmountUsed: data.loyaltyAmountUsed || 0,
            notes: data.notes,
            createdAt: data.createdAt?.toDate(),
            promotionId: data.promotionId,
            promotionDiscount: data.promotionDiscount || 0,
            listUnitPrice: data.listUnitPrice,
            commercialDiscount: data.commercialDiscount || 0,
            commercialDiscountReason: data.commercialDiscountReason,
            passDiscountTotal: data.passDiscountTotal || 0,
            passDiscountLabel: data.passDiscountLabel,
            // Champs d'archivage
            isArchived: data.isArchived || false,
            archivedAt: data.archivedAt?.toDate(),
            archiveReason: data.archiveReason,
            // Champs transaction
            transactionGroupId: data.transactionGroupId,
            transactionIndex: data.transactionIndex,
            receiptNumber: data.receiptNumber,
            // Suivi paiement (avec valeurs par défaut pour rétrocompatibilité)
            paymentPlanId: data.paymentPlanId,
            paymentStatus: data.paymentStatus || 'paid',
            amountPaid,
            amountRemaining,
            isDelivered: data.isDelivered ?? true,
            deliveredAt: data.deliveredAt?.toDate(),
            
            // ✅ NOUVEAUX : Champs TVA
            taxRateId: data.taxRateId,
            taxRateName: data.taxRateName,
            taxRate: data.taxRate,
            subtotalExcludingTax: data.subtotalExcludingTax,
            totalTaxAmount: data.totalTaxAmount,
            subtotalIncludingTax: data.subtotalIncludingTax,
            siteId: data.siteId,
            siteName: data.siteName,

            ...(data.soldAsPack
              ? {
                  packId: data.packId,
                  packInstanceId: data.packInstanceId,
                  packName: data.packName,
                  packPrice: data.packPrice,
                  packComponentIndex: data.packComponentIndex,
                  soldAsPack: true as const,
                }
              : {}),
          } as ProductSale;
        });

        // Filtrer les ventes archivées par défaut
        if (!filters?.includeArchived) {
          sales = sales.filter(sale => !sale.isArchived);
        }

        // Filtres côté client (pour les cas où Firebase ne peut pas filtrer directement)
        if (filters?.searchTerm) {
          sales = sales.filter(sale =>
            matchesSearchInFields(
              [sale.productName, sale.clientName, sale.employeeName],
              filters.searchTerm!
            )
          );
        }

        if (filters?.siteId) {
          sales = sales.filter((sale) => sale.siteId === filters.siteId);
        }

        // Tri selon le contexte
        if (filters?.transactionGroupId) {
          // Pour une transaction spécifique, trier par index
          sales.sort((a, b) => (a.transactionIndex || 0) - (b.transactionIndex || 0));
        } else {
          // Pour l'historique général, garder le tri par date décroissante
          // (déjà fait par la query orderBy dans la plupart des cas)
        }
        console.log('📊 Firebase returned', sales.length, 'sales before client-side filtering');
        console.log('📊 Sales', sales);
        console.log('👤 Sample employeeIds from results:', sales.slice(0, 3).map(s => ({ 
          productName: s.productName, 
          employeeId: s.employeeId, 
          employeeName: s.employeeName 
        })));

        return sales;
        
      } catch (error) {
        console.error('Erreur lors de la récupération des ventes:', error);
        throw error;
      }
    },
    enabled: !!businessId && (!!permissions?.canViewProductSales || !!permissions?.canViewInventoryReports),
  });
};

export const useAddProductSale = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, userProfile } = useAuth();
  const businessId = currentBusiness?.id;
  const defaultPosSite = useDefaultPointOfSaleSite(businessId);
  const applyPromotionMutation = useApplyPromotion();
  const createStockNotification = useCreateStockNotification();
  
  return useMutation({
    mutationFn: async (saleData: {
      productId: string;
      quantity: number;
      unitPrice: number;
      clientId?: string;
      clientName: string;
      employeeId: string;
      employeeName: string;
      date: Date;
      paymentMethod: PrestationPaymentMethod; // Conservé pour rétrocompatibilité
      paymentSplits?: PaymentSplit[]; // ✅ NOUVEAU : Support pour plusieurs moyens de paiement
      loyaltyAmountUsed?: number;
      loyaltyBonusGranted?: number;
      finalPrice: number;
      notes?: string;
      promotionId?: string;
      promotionDiscount?: number;
      listUnitPrice?: number;
      commercialDiscount?: number;
      commercialDiscountReason?: string;
      passDiscountTotal?: number;
      passDiscountLabel?: string;
      transactionGroupId?: string;
      transactionIndex?: number;
      receiptNumber: string;
      referentId?: string;
      referentName?: string;
      // TVA (envoyés par useTransaction quand le panier est enrichi)
      taxRateId?: string;
      taxRateName?: string;
      taxRate?: number;
      subtotalExcludingTax?: number;
      totalTaxAmount?: number;
      subtotalIncludingTax?: number;
      // ✅ NOUVEAU : Paramètres pour les plans de paiement
      paymentStatus?: ProductSalePaymentStatus;
      amountPaid?: number;
      amountRemaining?: number;
      isDelivered?: boolean;
      /** Multi-sites : site de vente (point de vente). */
      siteId?: string;
      siteName?: string;
      /** Pack catalogue (si vendu en forfait). */
      packId?: string;
      packInstanceId?: string;
      packName?: string;
      packPrice?: number;
      packComponentIndex?: number;
      soldAsPack?: boolean;
    }) => {
      if (!businessId || !userProfile) {
        throw new AppError('userUndefined');
      }

      if (!saleData.employeeId || !saleData.employeeName) {
        throw new AppError('employeeMissing');
      }

      // ✅ Vérifier que l'employee est actif
      const employeeRef = doc(db, COLLECTIONS.employees, saleData.employeeId);
      const employeeSnap = await getDoc(employeeRef);
      if (!employeeSnap.exists()) {
        throw new Error('Employé introuvable');
      }
      const employeeData = employeeSnap.data();
      if (employeeData.isActive !== true) {
        throw new Error('Cet employé est inactif. Impossible d\'enregistrer une vente.');
      }

      const subtotal = saleData.quantity * saleData.unitPrice;
      const totalPrice = saleData.finalPrice;
      if (totalPrice < 0) {
        throw new Error('Le prix final ne peut pas être négatif');
      }
      const loyaltyEnabled = isLoyaltyEnabled(currentBusiness);
      let loyaltyBonusGranted = saleData.loyaltyBonusGranted || 0;
      if (loyaltyEnabled && !loyaltyBonusGranted && saleData.clientId) {
        loyaltyBonusGranted = calculateLoyaltyBonus(totalPrice, currentBusiness);
      } else if (!loyaltyEnabled) {
        loyaltyBonusGranted = 0;
      }
      const loyaltyChange = (loyaltyBonusGranted || 0) - (saleData.loyaltyAmountUsed || 0);

      // ✅ Offline-first : écritures simples (getDoc / updateDoc / addDoc) pour que la persistance
      // Firestore mette les opérations en file hors ligne. Pas de runTransaction (nécessite le réseau).
      const productRef = doc(db, COLLECTIONS.products, saleData.productId);
      const productSnap = await getDoc(productRef);
      if (!productSnap.exists()) {
        throw new AppError('products.notFound');
      }
      const productDataTx = productSnap.data();
      if (productDataTx.businessId !== businessId) {
        throw new Error('Ce produit n\'appartient pas à votre institut');
      }
      const stockBySite = (productDataTx.stockBySite as Record<string, number>) || {};
      const resolvedSiteId = saleData.siteId || defaultPosSite?.id;
      const resolvedSiteName = saleData.siteName || defaultPosSite?.name;
      if (!resolvedSiteId || !resolvedSiteName) {
        throw new Error(getSalesSiteUiLabels(currentBusiness).missingSalesSite);
      }
      const availableAtSite = getProductStockAtSite(
        {
          currentStock: productDataTx.currentStock,
          stockBySite,
        },
        resolvedSiteId
      );
      if (saleData.quantity > availableAtSite) {
        throw new AppError('stock.insufficient', { available: availableAtSite });;
      }

      const productUpdate: Record<string, unknown> = {
        currentStock: increment(-saleData.quantity),
        updatedAt: Timestamp.now(),
      };
      if (resolvedSiteId) {
        productUpdate.stockBySite = {
          ...stockBySite,
          [resolvedSiteId]: Math.max(0, availableAtSite - saleData.quantity),
        };
      }
      await updateDoc(productRef, productUpdate as any);

      // ✅ NOUVEAU : Gérer paymentSplits et paymentMethod (rétrocompatibilité)
      let finalPaymentMethod = saleData.paymentMethod;
      let finalPaymentSplits: PaymentSplit[] | undefined = saleData.paymentSplits;

      // Si paymentSplits est présent, l'utiliser et calculer paymentMethod principal
      if (saleData.paymentSplits && saleData.paymentSplits.length > 0) {
        finalPaymentSplits = saleData.paymentSplits;
        finalPaymentMethod = getPrimaryPaymentMethod(saleData.paymentSplits) || saleData.paymentMethod;
      } else if (saleData.paymentMethod) {
        // Fallback : si seulement paymentMethod est présent, le conserver
        finalPaymentMethod = saleData.paymentMethod;
        finalPaymentSplits = undefined;
      }

      const newSale = {
        businessId,
        productId: saleData.productId,
        productName: getProductDisplayName(productDataTx),
        quantity: saleData.quantity,
        unitPrice: saleData.unitPrice,
        totalPrice: subtotal,
        finalPrice: totalPrice,
        clientId: saleData.clientId || null,
        clientName: saleData.clientName.trim(),
        employeeId: saleData.employeeId,
        employeeName: saleData.employeeName,
        date: Timestamp.fromDate(saleData.date),
        paymentMethod: finalPaymentMethod, // Conservé pour rétrocompatibilité
        ...(finalPaymentSplits && finalPaymentSplits.length > 0 ? { paymentSplits: finalPaymentSplits } : {}), // ✅ NOUVEAU : Sauvegarder paymentSplits si présent
        loyaltyAmountUsed: saleData.loyaltyAmountUsed || 0,
        loyaltyBonusGranted,
        receiptNumber: saleData.receiptNumber,
        // ✅ MODIFIÉ : Permettre de définir paymentStatus, amountPaid, amountRemaining, isDelivered
        // Valeurs par défaut pour une vente classique payée immédiatement
        paymentPlanId: null,
        paymentStatus: saleData.paymentStatus ?? 'paid',
        amountPaid: saleData.amountPaid ?? totalPrice,
        amountRemaining: saleData.amountRemaining ?? (saleData.paymentStatus === 'partial' ? Math.max(totalPrice - (saleData.amountPaid ?? 0), 0) : 0),
        isDelivered: saleData.isDelivered ?? (saleData.paymentStatus === 'partial' ? false : true),
        ...(saleData.notes?.trim() && { notes: saleData.notes.trim() }),
        ...(saleData.promotionId && { promotionId: saleData.promotionId }),
        promotionDiscount: saleData.promotionDiscount ?? 0,
        ...(saleData.listUnitPrice != null &&
          saleData.listUnitPrice > 0 && { listUnitPrice: saleData.listUnitPrice }),
        ...(saleData.commercialDiscount != null &&
          saleData.commercialDiscount > 0 && {
            commercialDiscount: saleData.commercialDiscount,
          }),
        ...(saleData.commercialDiscountReason?.trim() && {
          commercialDiscountReason: saleData.commercialDiscountReason.trim(),
        }),
        ...(saleData.passDiscountTotal != null &&
          saleData.passDiscountTotal > 0 && {
            passDiscountTotal: saleData.passDiscountTotal,
          }),
        ...(saleData.passDiscountLabel?.trim() && {
          passDiscountLabel: saleData.passDiscountLabel.trim(),
        }),
        siteId: resolvedSiteId,
        siteName: resolvedSiteName,
        isArchived: false,
        createdAt: Timestamp.now(),
        ...(saleData.transactionGroupId != null && {
          transactionGroupId: saleData.transactionGroupId,
          transactionIndex: saleData.transactionIndex,
        }),
        ...(saleData.referentId && saleData.referentName && {
          referentId: saleData.referentId,
          referentName: saleData.referentName,
        }),
        ...(saleData.soldAsPack
          ? {
              packId: saleData.packId,
              packInstanceId: saleData.packInstanceId,
              packName: saleData.packName,
              packPrice: saleData.packPrice,
              packComponentIndex: saleData.packComponentIndex,
              soldAsPack: true as const,
            }
          : {}),
        // TVA (pour comptabilité / reçus)
        ...(saleData.taxRateId != null && {
          taxRateId: saleData.taxRateId,
          ...(saleData.taxRateName != null && { taxRateName: saleData.taxRateName }),
          ...(saleData.taxRate != null && { taxRate: saleData.taxRate }),
          ...(saleData.subtotalExcludingTax != null && { subtotalExcludingTax: saleData.subtotalExcludingTax }),
          ...(saleData.totalTaxAmount != null && { totalTaxAmount: saleData.totalTaxAmount }),
          ...(saleData.subtotalIncludingTax != null && { subtotalIncludingTax: saleData.subtotalIncludingTax }),
        }),
      };
      const saleRef = await addDoc(collection(db, COLLECTIONS.productSales), newSale);
      const saleId = saleRef.id;

      await addDoc(collection(db, COLLECTIONS.stockMovements), {
        businessId,
        productId: saleData.productId,
        productName: getProductDisplayName(productDataTx),
        type: 'exit',
        quantity: saleData.quantity,
        // Pour les ventes, afficher le prix de vente (et non le coût d'achat produit)
        unitCost: saleData.unitPrice,
        totalCost: totalPrice,
        reason: 'Vente produit',
        reference: `VENTE-${saleId}`,
        date: Timestamp.fromDate(saleData.date),
        employeeId: saleData.employeeId,
        employeeName: saleData.employeeName,
        notes: `Vente à ${saleData.clientName}`,
        siteId: resolvedSiteId,
        siteName: resolvedSiteName,
        createdAt: Timestamp.now(),
      });

      // Mettre à jour le client : cagnotte (si nécessaire) + dernière visite
      if (saleData.clientId) {
        const clientRef = doc(db, COLLECTIONS.clients, saleData.clientId);
        const updateData: any = {
          lastVisitDate: Timestamp.fromDate(saleData.date),
          updatedAt: Timestamp.now(),
        };
        
        // Mettre à jour la cagnotte si nécessaire
        if (loyaltyEnabled && loyaltyChange !== 0) {
          updateData.loyaltyBalance = increment(loyaltyChange);
        }
        
        await updateDoc(clientRef, updateData);
      }

      const newStockAtSite = Math.max(0, availableAtSite - saleData.quantity);
      const productData = productDataTx;

      // Synchroniser l'alerte de stock sur le lieu impacté
      try {
        const thresholdAtSite = getStockAlertThresholdForSite(productData, resolvedSiteId);
        await createStockNotification.mutateAsync({
          productId: saleData.productId,
          productName: getProductDisplayName(productData),
          currentStock: newStockAtSite,
          minStockAlert: thresholdAtSite,
          siteId: resolvedSiteId,
          siteName: resolvedSiteName,
        });
      } catch (error) {
        console.warn('Erreur lors de la synchronisation de la notification de stock:', error);
      }

      if (saleData.promotionId) {
        try {
          const promotionRef = doc(db, COLLECTIONS.promotions, saleData.promotionId);
          const promotionSnap = await getDoc(promotionRef);
          if (promotionSnap.exists()) {
            const promotionData = promotionSnap.data();
            if (promotionData.businessId === businessId) {
              if (shouldApplyMarketingPromotionForLine(saleData)) {
                await applyPromotionMutation.mutateAsync({
                  promotionId: saleData.promotionId,
                  prestationId: saleId,
                  revenueAmount: marketingRevenueForPackAwareLine({
                    ...saleData,
                    finalPrice: totalPrice,
                  }),
                });
              }
            }
          }
        } catch (promotionError) {
          console.error('Erreur lors de l\'application de la promotion:', promotionError);
        }
      }

      return saleId;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'productSales', businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'stockMovements', businessId] });
      queryClient.invalidateQueries({ queryKey: ['business', 'clients', businessId] });
      queryClient.invalidateQueries({ queryKey: ['marketing', 'promotions'] });
      queryClient.invalidateQueries({ queryKey: ['marketing', 'campaigns'] });
      
      if (variables.transactionGroupId) {
        queryClient.invalidateQueries({ 
          queryKey: ['transaction', 'items', businessId, variables.transactionGroupId] 
        });
      }
    },
  });
};

export const useUpdateProductSale = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;

  return useMutation({
    mutationFn: async ({ id, date, paymentPlanId, paymentStatus, amountPaid, amountRemaining, isDelivered }: { 
      id: string; 
      date?: Date;
      paymentPlanId?: string;
      paymentStatus?: 'unpaid' | 'partial' | 'paid';
      amountPaid?: number;
      amountRemaining?: number;
      isDelivered?: boolean;
    }) => {
      if (!businessId) {
        throw new AppError('businessUndefined');
      }

      const saleRef = doc(db, COLLECTIONS.productSales, id);
      const saleSnap = await getDoc(saleRef);

      if (!saleSnap.exists()) {
        throw new Error('Vente introuvable');
      }

      const saleData = saleSnap.data();
      if (saleData.businessId !== businessId) {
        throw new Error("Cette vente n'appartient pas à votre business");
      }

      const updateData: any = {};
      if (date !== undefined) {
        updateData.date = Timestamp.fromDate(date);
      }
      if (paymentPlanId !== undefined) {
        updateData.paymentPlanId = paymentPlanId;
      }
      if (paymentStatus !== undefined) {
        updateData.paymentStatus = paymentStatus;
      }
      if (amountPaid !== undefined) {
        updateData.amountPaid = amountPaid;
      }
      if (amountRemaining !== undefined) {
        updateData.amountRemaining = amountRemaining;
      }
      if (isDelivered !== undefined) {
        updateData.isDelivered = isDelivered;
        if (isDelivered) {
          updateData.deliveredAt = Timestamp.now();
        }
      }

      await updateDoc(saleRef, updateData);

      return { id, ...updateData };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'productSales', businessId] });
    },
  });
};

export const useArchiveProductSale = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async (saleToArchive: { 
      id: string; 
      employeeId: string; 
      clientId?: string;
      reason: string;
      lifecycleStatus?: 'voided' | 'superseded' | 'credited';
      correctionEventId?: string;
      supersededByTransactionGroupId?: string;
      strictStockRestore?: boolean;
    }) => {
      const saleRef = doc(db, COLLECTIONS.productSales, saleToArchive.id);
      const saleSnap = await getDoc(saleRef);
      
      if (!saleSnap.exists()) {
        throw new Error('Vente introuvable');
      }
      
      const sale = saleSnap.data() as ProductSale;
      
      if (sale.businessId !== businessId) {
        throw new Error('Cette vente n\'appartient pas à votre business');
      }

      const lifecycleStatus = saleToArchive.lifecycleStatus || 'voided';
      const existingEventIds = Array.isArray((sale as any).correctionEventIds)
        ? ([...(sale as any).correctionEventIds] as string[])
        : [];
      if (saleToArchive.correctionEventId) {
        existingEventIds.push(saleToArchive.correctionEventId);
      }
      
      await updateDoc(saleRef, {
        isArchived: true,
        archivedAt: Timestamp.now(),
        archiveReason: saleToArchive.reason,
        lifecycleStatus,
        ...(existingEventIds.length ? { correctionEventIds: existingEventIds } : {}),
        ...(saleToArchive.supersededByTransactionGroupId
          ? {
              supersededByTransactionGroupId:
                saleToArchive.supersededByTransactionGroupId,
            }
          : {}),
        notes: sale.notes 
          ? `${sale.notes}\n\n--- ARCHIVÉE ---\nRaison: ${saleToArchive.reason}`
          : `--- ARCHIVÉE ---\nRaison: ${saleToArchive.reason}`
      });
      
      // Restaurer le stock du produit
      const productRef = doc(db, COLLECTIONS.products, sale.productId);
      const productSnap = await getDoc(productRef);
      
      if (!productSnap.exists()) {
        if (saleToArchive.strictStockRestore) {
          throw new Error(`Produit introuvable pour restauration stock (${sale.productId}).`);
        }
      } else {
        const productData = productSnap.data();
        if (productData.businessId !== businessId) {
          if (saleToArchive.strictStockRestore) {
            throw new Error('Produit hors périmètre business lors de la restauration stock.');
          }
        } else {
          const archiveUnitCostRaw = Number(productData.averageCost ?? productData.costPrice ?? 0);
          const archiveUnitCost = Number.isFinite(archiveUnitCostRaw) ? Math.max(0, archiveUnitCostRaw) : 0;
          const productUpdate: Record<string, unknown> = {
            currentStock: increment(sale.quantity),
            updatedAt: Timestamp.now(),
          };
          if (sale.siteId) {
            const stockBySite = (productData.stockBySite as Record<string, number>) || {};
            const currentAtSite = getProductStockAtSite(
              { currentStock: productData.currentStock, stockBySite },
              sale.siteId
            );
            productUpdate.stockBySite = {
              ...stockBySite,
              [sale.siteId]: currentAtSite + sale.quantity,
            };
          }
          await updateDoc(productRef, productUpdate as any);
          
          const movementRef = collection(db, COLLECTIONS.stockMovements);
          await addDoc(movementRef, {
            businessId,
            productId: sale.productId,
            productName: sale.productName,
            type: 'adjustment',
            quantity: sale.quantity,
            unitCost: archiveUnitCost,
            totalCost: archiveUnitCost * sale.quantity,
            reason: 'Archivage vente',
            reference: `ARCH-VENTE-${sale.id}`,
            date: Timestamp.now(),
            employeeId: saleToArchive.employeeId,
            employeeName: sale.employeeName,
            notes: `Restauration stock suite à archivage vente: ${saleToArchive.reason}`,
            ...(sale.siteId && sale.siteName ? { siteId: sale.siteId, siteName: sale.siteName } : {}),
            createdAt: Timestamp.now(),
          });
        }
      }
      
      // ✅ NOUVEAU : Vérifier si la cagnotte est activée avant de restaurer
      const loyaltyEnabled = isLoyaltyEnabled(currentBusiness);
      
      // Ajuster la cagnotte du client (seulement si activée)
      const loyaltyBonusToRemove = sale.loyaltyBonusGranted || 0;
      const loyaltyUsageToRestore = sale.loyaltyAmountUsed || 0;
      
      if (loyaltyEnabled && (loyaltyBonusToRemove > 0 || loyaltyUsageToRestore > 0) && sale.clientId) {
        const clientRef = doc(db, COLLECTIONS.clients, sale.clientId);
        const clientSnap = await getDoc(clientRef);
        
        if (clientSnap.exists()) {
          const currentClient = clientSnap.data() as Client;
          
          if (currentClient.businessId === businessId) {
            const newLoyaltyBalance = Math.max(0, 
              (currentClient.loyaltyBalance || 0) + loyaltyUsageToRestore - loyaltyBonusToRemove
            );
            
            await updateDoc(clientRef, {
              loyaltyBalance: newLoyaltyBalance
            });
          }
        }
      }
      
      // Restaurer la promotion selon le usageType
      if (sale.promotionId) {
        try {
          const promotionRef = doc(db, COLLECTIONS.promotions, sale.promotionId);
          const promotionSnap = await getDoc(promotionRef);
          
          if (promotionSnap.exists()) {
            const promotionData = promotionSnap.data();
            
            if (promotionData.businessId === businessId) {
              const campaignRef = doc(db, COLLECTIONS.campaigns, promotionData.campaignId);
              const campaignSnap = await getDoc(campaignRef);
              
              if (campaignSnap.exists()) {
                const campaignData = campaignSnap.data();
                const usageType = campaignData.usageType || 'single_use';
                
                console.log(`Archivage vente: restauration promotion ${sale.promotionId}, usageType=${usageType}`);
                
                if (usageType === 'single_use') {
                  await updateDoc(promotionRef, {
                    used: false,
                    usedAt: null,
                    prestationId: null,
                    active: true,
                    usageCount: Math.max(0, (promotionData.usageCount || 1) - 1),
                  });
                } else {
                  const newUsageCount = Math.max(0, (promotionData.usageCount || 1) - 1);
                  await updateDoc(promotionRef, {
                    usageCount: newUsageCount,
                    ...(newUsageCount === 0 && { lastUsedAt: null }),
                  });
                }
                
                await updateDoc(campaignRef, {
                  'stats.conversionsCount': increment(-1),
                  'stats.revenueGenerated': increment(-(sale.finalPrice || sale.totalPrice)),
                });
              }
            }
          }
        } catch (promotionError) {
          console.error('Erreur lors de la restauration de la promotion:', promotionError);
        }
      }
      
      // Annuler le plan de paiement lié (soft) — conserver l’historique des versements
      if (sale.paymentPlanId) {
        try {
          const planRef = doc(db, COLLECTIONS.paymentPlans, sale.paymentPlanId);
          const planSnap = await getDoc(planRef);
          
          if (planSnap.exists()) {
            const planData = planSnap.data();
            if (planData.businessId === businessId && planData.status !== 'cancelled') {
              await updateDoc(planRef, {
                status: 'cancelled',
                updatedAt: Timestamp.now(),
                cancelReason: saleToArchive.reason,
              });
            }
          }
        } catch (paymentPlanError) {
          console.error('Erreur lors de l\'annulation du plan de paiement:', paymentPlanError);
          // Ne pas faire échouer l'archivage si l'annulation du plan échoue
        }
      }
      
      // ✅ NOUVEAU : Supprimer la référence (référent) si elle existe
      if (sale.referentId) {
        try {
          const referencesQuery = query(
            collection(db, COLLECTIONS.references),
            where('businessId', '==', businessId),
            where('saleId', '==', saleToArchive.id),
            where('saleType', '==', 'product')
          );
          
          const referencesSnapshot = await getDocs(referencesQuery);
          for (const refDoc of referencesSnapshot.docs) {
            await deleteDoc(doc(db, COLLECTIONS.references, refDoc.id));
          }
        } catch (referenceError) {
          console.error('Erreur lors de la suppression de la référence:', referenceError);
          // Ne pas faire échouer l'archivage si la suppression de la référence échoue
        }
      }
      
      return saleToArchive;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'productSales', businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'stockMovements', businessId] });
      
      if (data.clientId) {
        queryClient.invalidateQueries({ queryKey: ['business', 'clients', businessId] });
      }
      
      queryClient.invalidateQueries({ queryKey: ['marketing', 'promotions'] });
      queryClient.invalidateQueries({ queryKey: ['marketing', 'campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['clients', 'paymentPlans'] });
      queryClient.invalidateQueries({ queryKey: ['clients', 'paymentPlanPayments'] });
      queryClient.invalidateQueries({ queryKey: ['references', businessId] });
    },
  });
};

export const useProductSalesByProduct = (productId: string, limitCount?: number) => {
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['inventory', 'productSales', 'byProduct', businessId, productId, limitCount, ],
    queryFn: async () => {
      if (!productId || !businessId) {
        return [];
      }

      try {
        let q = query(
          collection(db, COLLECTIONS.productSales),
          where('businessId', '==', businessId),
          where('productId', '==', productId),
          orderBy('date', 'desc')
        );

        if (limitCount) {
          q = query(q, limit(limitCount));
        }

        const snapshot = await getDocs(q).catch(error => {
          handleFirestoreError(error, 'useProductSales - Main query');
          throw error;
        });
        
        return snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            businessId: data.businessId,
            productId: data.productId,
            productName: data.productName,
            quantity: data.quantity,
            unitPrice: data.unitPrice,
            totalPrice: data.totalPrice,
            finalPrice: data.finalPrice || data.totalPrice,
            clientId: data.clientId,
            clientName: data.clientName,
            employeeId: data.employeeId,
            employeeName: data.employeeName,
            date: data.date?.toDate(),
            paymentMethod: data.paymentMethod,
            paymentSplits: data.paymentSplits as PaymentSplit[] | undefined, // ✅ NOUVEAU : Récupérer paymentSplits depuis Firestore
            loyaltyBonusGranted: data.loyaltyBonusGranted || 0,
            loyaltyAmountUsed: data.loyaltyAmountUsed || 0,
            notes: data.notes,
            createdAt: data.createdAt?.toDate(),
            promotionId: data.promotionId,
            promotionDiscount: data.promotionDiscount || 0,
            listUnitPrice: data.listUnitPrice,
            commercialDiscount: data.commercialDiscount || 0,
            commercialDiscountReason: data.commercialDiscountReason,
            passDiscountTotal: data.passDiscountTotal || 0,
            passDiscountLabel: data.passDiscountLabel,
            isArchived: data.isArchived || false,
            archivedAt: data.archivedAt?.toDate(),
            archiveReason: data.archiveReason,
            transactionGroupId: data.transactionGroupId,
            transactionIndex: data.transactionIndex,
            receiptNumber: data.receiptNumber,
            
            // ✅ NOUVEAUX : Champs TVA
            taxRateId: data.taxRateId,
            taxRateName: data.taxRateName,
            taxRate: data.taxRate,
            subtotalExcludingTax: data.subtotalExcludingTax,
            totalTaxAmount: data.totalTaxAmount,
            subtotalIncludingTax: data.subtotalIncludingTax,
            siteId: data.siteId,
            siteName: data.siteName,
          } as ProductSale;
        });
        
      } catch (error) {
        console.error('Erreur lors de la récupération des ventes du produit:', error);
        return [];
      }
    },
    enabled: !!productId && !!businessId,
  });
};

export const useProductSalesStats = (dateRange?: [Date, Date]) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['inventory', 'productSales', 'stats', businessId, dateRange, ],
    queryFn: async () => {
      if (!businessId) {
        return {
          totalSales: 0,
          totalRevenue: 0,
          totalQuantitySold: 0,
          totalLoyaltyGranted: 0,
          averageSaleValue: 0,
        };
      }

      try {
        let q = query(
          collection(db, COLLECTIONS.productSales),
          where('businessId', '==', businessId),
          orderBy('date', 'desc')
        );

        if (dateRange) {
          q = query(
            q,
            where('date', '>=', Timestamp.fromDate(dateRange[0])),
            where('date', '<=', Timestamp.fromDate(dateRange[1]))
          );
        }

        const snapshot = await getDocs(q).catch(error => {
          handleFirestoreError(error, 'useProductSales - Main query');
          throw error;
        });
        
        let totalSales = 0;
        let totalRevenue = 0;
        let totalQuantitySold = 0;
        let totalLoyaltyGranted = 0;

        snapshot.docs.forEach(doc => {
          const data = doc.data();
          if (!data.isArchived) {
            totalSales += 1;
            totalRevenue += billedTtcForProductSale({
              finalPrice: data.finalPrice,
              totalPrice: data.totalPrice,
              subtotalIncludingTax: data.subtotalIncludingTax,
            });
            totalQuantitySold += data.quantity || 0;
            totalLoyaltyGranted += data.loyaltyBonusGranted || 0;
          }
        });

        const averageSaleValue = averageAmount(totalRevenue, totalSales);

        return {
          totalSales,
          totalRevenue,
          totalQuantitySold,
          totalLoyaltyGranted,
          averageSaleValue,
        };
        
      } catch (error) {
        console.error('Erreur lors du calcul des statistiques de ventes:', error);
        return {
          totalSales: 0,
          totalRevenue: 0,
          totalQuantitySold: 0,
          totalLoyaltyGranted: 0,
          averageSaleValue: 0,
        };
      }
    },
    enabled: !!businessId && (!!permissions?.canViewProductSales || !!permissions?.canViewInventoryReports),
  });
};